import * as THREE from "three";

import {
  type AftermathFeature,
  FEATURE_BATTLEFIELD,
  FEATURE_BURNED_FARMSTEAD,
  FEATURE_GRAVE,
  FEATURE_MARKET_BOARD,
} from "../world/types";

/*
 * The prop registry: every object in the scene that is not terrain.
 *
 * `chunkMeshes.ts` owns the world's slabs and this owns everything standing on
 * them — bodies, grave markers, the market board, a burned farmstead, a
 * battlefield cairn, a knot of refugees, and the name label floating over each
 * of them. One module, for the reason the chunk registry is one module: it is
 * the only place that knows both what the client wants drawn and Three.js, and
 * it is deliberately dumb about the first half. It is handed a list of
 * {@link SceneProp} and it makes the scene match; it never asks where a prop
 * came from and it never decides where one should be.
 *
 * # The look: boxes, vertex colours, no assets
 *
 * Everything here is built out of `BoxGeometry` and baked per-vertex colour,
 * uploaded through the same one-`MeshLambertMaterial`-with-vertex-colours the
 * terrain uses (`render/scene.ts`, "The look"). That is Gate A's Prototype 0.5
 * pass held to exactly (`docs/plans/gate-a.md`): flat shading, no textures, no
 * post-processing, and — the part that matters for a prototype that has to run
 * from a `git clone` — **no external asset of any kind**. A box has one normal
 * per face, so flat shading falls out of the geometry rather than out of a
 * material flag, and the props sit in the same light as the hillside behind
 * them.
 *
 * Wave 2B did the art pass wave 1B left it (`docs/plans/phase-3-plan.md`), and
 * three rules came out of it that are worth stating before the numbers:
 *
 * - **Silhouette first.** Flat shading with no shadows gives the eye an outline
 *   and a value and nothing else, so every prop is built around one shape that
 *   is legible at twenty tiles — a chimney stack standing out of a burned
 *   footprint, a stepped cairn, a roofed board, a headstone with a mound in
 *   front of it. Detail below that scale is decoration and is kept cheap.
 * - **Value separates the prop from the ground.** The world is desaturated
 *   browns and greys (`data/materials.toml`: soil `5c4630`, granite `7a7670`),
 *   so a prop meant to be *found* is lighter than the hillside behind it and a
 *   prop meant to be *grim* is much darker. Nothing sits at the ground's value.
 * - **One saturated accent per prop, at most.** Embers in the ash, a blanket in
 *   a refugee column, moss on an old grave. More than one and the scene starts
 *   reading as a toy box.
 *
 * All of it is judged on screenshots and expected to move, which is why every
 * colour is a named entry in {@link PALETTE} rather than a literal in a box
 * list — the same arrangement `crates/mesher/src/color.rs` keeps its art knobs
 * in, for the same reason.
 *
 * Name labels are canvas-texture sprites for the same reason: a font file is an
 * asset, `CanvasRenderingContext2D` is not. The textures are cached by their
 * text, so ten bodies with one name cost one canvas between them.
 *
 * # The draw-call budget
 *
 * One mesh per prop and one sprite per label, both drawn. The measured scene
 * sits at 29–41 calls walking (README, "Performance, measured") against the
 * ~400 the rendering research calls comfortable
 * (`docs/research/rendering/threejs-chunks.md` §1), so a settlement's worth of
 * props — a dozen bodies, a handful of features, their labels — is thirty-odd
 * calls into a budget with room for ten times that. Instancing would buy
 * nothing yet and would cost the per-prop colour that makes a crowd read as a
 * crowd; when the number climbs past a hundred, `InstancedMesh` per kind is the
 * shape of the fix and this module is the only file that changes.
 *
 * # Geometry is shared, meshes are not
 *
 * A geometry is built once per `(kind, palette)` and handed to every prop that
 * wants it, so twelve bodies in four tunic colours are four geometries. Meshes
 * are per-prop because a mesh is a transform, and the transform is the only
 * thing that changes when somebody takes a step: `matrixAutoUpdate` is off and
 * the matrix is written on a move, which is `render/highlight.ts`'s rule for a
 * reused placed object.
 */

/**
 * What a prop is drawn as. A rendering choice: the simulation has no such
 * vocabulary.
 *
 * Phase 4 wave 1B adds two the phase's own decisions ask for, and each one is
 * presentation of something the simulation already said: a **signpost** at a
 * road mouth naming where the road goes (decision 4 — `Roads` drawn, nothing
 * authored), and the **smoke** over a town a battle record says was stormed
 * this morning (decision 5 — reaction, never reenactment). A third, a **caravan**
 * drawn along the chord between two towns, is gone: trade is an abstract pulse
 * with no position, and bodies come back to the roads as columns.
 *
 * Phase 5 wave 4A adds two more, on the same terms. A **chronicle** is a book
 * somebody left where it can be found — the simulation's own `Chronicles`
 * container, drawn where it says the book lies (decision 5), and drawn for a
 * ruler's book exactly as for the player's, because the simulation leaves its
 * own. A **ruin** is the wreckage of the last army through a town, drawn only
 * where `ruins()` says a town is still in one (decision 11): interim presentation
 * of a flag the log justifies, and never structural damage in world data — that
 * is Phase 7's, per the owner's recorded art direction.
 */
/*
 * Phase 7 wave 1F adds three, and every one of them is presentation of a fact
 * the simulation states rather than a thing the client decided:
 *
 * - **`toil`** stands at a work order's site (`WitnessSource.jobs`) and says
 *   what kind of work is happening there — a pick at a seam, an axe at a trunk,
 *   a bench at a workshop. It is a *marker*, not a body: who is doing the work
 *   is an `npc` drawn at the position the local tier gives them, and this is
 *   the tool in their hands seen from twenty tiles away. Its `facing` is rocked
 *   by the caller, which is the whole of the "animation-lite" the phase asks
 *   for (`witness/scenes.ts`).
 * - **`goods`** is something lying on a tile (`WitnessSource.tileItems`) — the
 *   spoil of a dig, a felled log, whatever somebody put down. Four colourways
 *   for four families of material and a fifth for a body, because a heap of
 *   rock and a bundle of greenery should not be one grey box.
 * - **`shelf`** is a library's shelf, which is an *item* in the simulation
 *   (`sim_core::ItemKind::Shelf`) standing on the library's own work tile. It
 *   is drawn rather than left as a `goods` heap because it is the one item in
 *   the world a player walks up to and reads, and the empty-shelf invitation is
 *   about being able to see that there is one (plan, decision 14).
 */
export type PropKind =
  | "npc"
  | "grave"
  | "market-board"
  | "burned-farmstead"
  | "battlefield"
  | "refugees"
  | "signpost"
  | "town-board"
  | "smoke"
  | "chronicle"
  | "ruin"
  | "toil"
  | "goods"
  | "body"
  | "shelf";

/**
 * Which life stage a body is drawn at.
 *
 * **The simulation's four rows, not the client's two** (`sim_core::body`, wave
 * 2A). It was `"adult" | "child"` and client-only, because the simulation said
 * only who was grown and *how big that made somebody* was body-plan data that
 * did not exist; the body plan exists now, so the vocabulary is the stage
 * table's ({@link WitnessSource.lifeStageWord}) and **the size that goes with
 * each row crosses the wall as a number** ({@link SceneProp.scale}).
 *
 * What this type is still for is the geometry cache: two bodies at one stage
 * share one set of boxes exactly as two bodies in one tunic do, and a word is a
 * better key than a float. Nothing in this file decides what a stage *means*.
 */
export type BodyStage = "baby" | "toddler" | "child" | "adult";

/** One thing to draw, as the caller describes it. */
export interface SceneProp {
  /** Stable while the thing exists. Two props with one id are one prop. */
  readonly id: string;

  readonly kind: PropKind;

  /** Feet, in world tiles: the origin of the geometry sits on the ground. */
  readonly x: number;
  readonly y: number;
  readonly z: number;

  /** Which way it is turned, in radians. Yaw 0 faces +y (`player/controller.ts`). */
  readonly facing: number;

  /** The name floating above it, or `null` for a prop that is not labelled. */
  readonly label: string | null;

  /**
   * Which of the kind's colourways to use.
   *
   * Taken modulo the kind's own variant count ({@link VARIANT_COUNT}), so a
   * caller can hand it a figure id or a feature id and get a stable tunic — or a
   * stable headstone — without knowing how many of either exist.
   */
  readonly palette: number;

  /**
   * Which life stage a body is drawn at, or omitted for a prop that is not a
   * body.
   *
   * Only `npc` builds anything different for it ({@link npcBoxes}); every other
   * kind is a thing rather than a person and ignores it, so a stage handed to a
   * headstone costs one duplicate geometry of the same boxes and never a wrong
   * picture. Omitted means grown, which is what every caller but
   * `witness/scenes.ts`'s `bodyPieces` says.
   */
  readonly stage?: BodyStage;

  /**
   * How tall the body is drawn, in parts per thousand of a grown one, or
   * omitted for a prop that is not a body.
   *
   * **The simulation's number, and the whole of what this file knows about how
   * big a person is** ({@link WitnessSource.bodyScale}, wave 2A): the body plan
   * owns the size curve, and until it existed this file authored two constants
   * for *child* and called them a stub with this as the named replacement (the
   * plan's chunk-I amendments, owner-ruled 2026-08-29).
   *
   * Omitted means a thousand — grown — which is what every caller but
   * `witness/scenes.ts`'s `bodyPieces` says.
   */
  readonly scale?: number;
}

/**
 * How high above a **grown** prop's feet its name floats, in tiles.
 *
 * Just clear of a grown body's head (1.58 tiles of geometry below, which is
 * {@link NPC_NECK_TILES} and the cube on top of it), so the sprite and the head
 * never overlap and `depthTest` can stay on — a label that shone through the
 * hill in front of it would be worse than one that disappears behind it, which
 * is `render/highlight.ts`'s call for the same trade.
 *
 * Exported since chunk D3 for the tracked mark, which hangs at exactly this
 * height so that a mark and the plate under it are on one line (`dev/track.ts`).
 * A second copy of the number in the file that draws the mark would be a second
 * thing to move the day this one moves.
 *
 * **It is one number for a grown body only, since chunk Fx-7.** A body drawn
 * small carries its plate at {@link plateHeightFor}'s answer instead — this one
 * floated a third of a tile over a child's head, because it was written when
 * every body was the same size.
 */
export const LABEL_HEIGHT_TILES = 1.95;

/** How tall a label is drawn in the world, in tiles. Width follows the text. */
const LABEL_SIZE_TILES = 0.26;

/**
 * The widest a label may be drawn, in tiles. Longer text shrinks to fit it.
 *
 * Width follows the text, and a name is short — but "refugees from Stonehearth
 * (41)" is thirty characters, which at the height above is nearly four tiles of
 * plate hanging over a group that is one tile wide. On a screenshot taken from
 * conversation distance that is a banner across the frame with the subject
 * behind it.
 *
 * Capping the *width* rather than shortening the text keeps the label saying
 * what it is for — how many people, and from where, which is data off the record
 * and not decoration — and costs only that a long label is drawn smaller than a
 * short one, which is what a signwriter would do with the same board.
 */
const LABEL_MAX_WIDTH_TILES = 1.7;

/** Pixels of canvas per tile of label. Two-ish device pixels at conversation range. */
const LABEL_PIXELS_PER_TILE = 220;

/** The label's ink and its plate, both sRGB. Chrome, not art. */
const LABEL_INK = "#f2f6fa";
const LABEL_PLATE = "rgba(12, 16, 22, 0.62)";

/**
 * Every colour a prop is built from, in sRGB. **The art knob for this module.**
 *
 * One table rather than literals scattered through {@link boxesFor}, because
 * these are judged on screen and moved as a set: a stone that reads against the
 * hillside at spawn and disappears against a terrace two chunks away is a
 * palette problem, and a palette problem is only fixable where the palette is.
 *
 * Grouped by material rather than by prop, so that the grave's stone and the
 * cairn's stone are visibly the same rock and a change to one is a change to
 * both. The values are picked against the world they stand in — soil `5c4630`,
 * granite `7a7670`, an oak `8f6e46`, under a warm sun and a blue ambient
 * (`render/scene.ts`) — and against the fog they fade into, `c3d8ee`.
 */
const PALETTE = {
  /** Cut stone: a headstone, a chimney, a cairn. Lighter than granite, so it is found. */
  stonePale: 0xbcb7ab,
  stoneFace: 0xaea89c,
  stoneShade: 0x968f84,
  stoneBase: 0x827b72,

  /** The same rock left rough and weathered: a cairn, kerb stones, a scorched stack. */
  cairnPale: 0xb0a99d,
  cairnLit: 0x9e978b,
  cairnMid: 0x8b8479,
  cairnDark: 0x77716a,

  /**
   * What grows back over a grave nobody has visited in a year. The one green in
   * the file, and a dull one: the first pass at this was a saturated moss green
   * and on screen it read as a rug thrown over the plot rather than as ground.
   * Flat shading has no texture to break a colour up, so a whole tile of green
   * has to sit close to the soil it replaces (`data/materials.toml`, `5c4630`).
   */
  moss: 0x5e5c39,

  /** Turned earth, still dark: a burial that happened this season. */
  earthFresh: 0x4a3728,

  /** Worked timber — posts, rafters, a spear shaft. Darker than the oak they were cut from. */
  timberDark: 0x5d4830,
  timberFace: 0xb79a6c,

  /** Paper nailed to a board. The brightest thing on any prop, and deliberately. */
  paperPale: 0xe8dfca,
  paperMid: 0xd9ccb0,
  paperWorn: 0xc7b593,

  /**
   * A bound cover, waxed and darkened by handling.
   *
   * Darker than any timber in the set, so the pale pages between the boards read
   * as *pages* rather than as another plank: a book at four paces is two bright
   * leaves inside a dark edge, and that contrast is the whole silhouette.
   */
  bookCover: 0x3f2a1b,

  /** Iron left out in the weather: a spearhead, a hinge. */
  ironDull: 0x8a8b8f,

  /** What a fire leaves. Four values, because char with one value is a hole in the ground. */
  charDeep: 0x241d19,
  charMid: 0x342a23,
  charSoot: 0x453931,
  ashPale: 0x6a625a,

  /** Stone a house burned around: the cairn's rock, dropped towards the soot on it. */
  stackSoot: 0x5c564e,

  /** The one warm thing in a burned farmstead, and the reason it does not read as rubble. */
  ember: 0x7d3117,

  /** Skin. Two tones so a crowd is not one family. */
  skinFair: 0xb08968,
  skinDark: 0x9a7454,

  /** What a body wears under a tunic: trousers, boots. */
  trouser: 0x5b5041,

  /**
   * Refugee cloth — duller than a townsman's tunic, which is the point, but not
   * darker: the first pass took the value down as well as the saturation and a
   * column on the road read as four black planks. Flat shading gives a body
   * nothing but its own value, so "drab" has to be spelled without it.
   */
  clothWorn: 0x7f7566,
  clothDust: 0x8b8070,
  clothDim: 0x70695c,
  clothPale: 0x968b7b,

  /** A bundle, and the blanket rolled on top of it: the group's one accent. */
  bundle: 0xa08a63,
  blanket: 0x8a4a38,

  /**
   * A dyed ribbon down a book's spine, and the one cold colour in the file.
   *
   * Cold on purpose. Every other accent here is a fire or a blanket — warm,
   * cheap, carried — and a book is the one thing drawn that somebody *paid* for,
   * so the thing that catches the eye on it is the thing that cost money.
   */
  dyedCloth: 0x3f6b6b,

  /**
   * A town burning, seen from a road away. Four values from soot to a pale head,
   * because a plume with one value is a wall.
   *
   * Warm-grey rather than neutral: the sky it stands against is a cold blue
   * (`render/scene.ts`) and a neutral grey column at fifteen tiles tall
   * disappeared into it in the first pass. It also has to survive the fog it is
   * seen through — this is the only prop in the set drawn at thirty tiles or
   * more by design — so the values are separated wider than anything else here.
   */
  smokeSoot: 0x453f38,
  smokeMid: 0x5d564c,
  smokeLit: 0x777063,
  smokeHead: 0x8d8578,
} as const;

/**
 * Tunic colours for bodies, in sRGB. Enough that a small crowd is not a uniform.
 *
 * Lighter than the first guess for a reason worth writing down: flat shading
 * with no shadows gives a body nothing but its own value to be read by, and a
 * mid-brown tunic that looks right in a swatch reads as a silhouette against the
 * terraced ground. Judged on screen in wave 2B alongside the rest of
 * {@link PALETTE} and left where wave 1B put them — they are the one part of the
 * prop set that was already doing its job.
 */
const NPC_TUNICS = [0xa88763, 0x86a074, 0x9b747a, 0x6e8199, 0xa89765, 0x847885];

/**
 * How many colourways each kind is built in.
 *
 * A prop's `palette` is taken modulo this, so a caller can hand it a figure id
 * or a feature id and get something stable without knowing how many variants
 * exist. Two things ride on it being *per kind* rather than one global count:
 * a kind with one variant builds one geometry however many of it are on the
 * ground (five identical cairns cost one), and two graves side by side get
 * different stone rather than being the same object twice.
 *
 * The variants are cosmetic and never semantic. The client does not get to
 * decide that a grave is old because the log says the death was old — reading
 * meaning out of a record and drawing it is exactly the decision that belongs on
 * the other side of the seam (devlog #2).
 */
const VARIANT_COUNT: Readonly<Record<PropKind, number>> = {
  npc: NPC_TUNICS.length,
  grave: 2,
  "market-board": 1,
  "burned-farmstead": 1,
  battlefield: 1,
  refugees: 2,
  // A signpost is carpentry and looks the same wherever it stands; smoke gets
  // one, because it is drawn at thirty tiles through fog and a second colourway
  // would be a variant nobody can see.
  signpost: 1,
  // One per town and towns are never side by side, so a second colourway would
  // never share a screenshot with the first.
  "town-board": 1,
  smoke: 1,
  // Two books, because a town that has two of them stands them a few paces
  // apart and the curated world has exactly that (`session/world.ts`): one
  // stone freshly cut and one weathered is the difference between two books and
  // one book drawn twice.
  chronicle: 2,
  // Three, because a ruin is drawn as a *ring* of wreckage round a town centre
  // and five identical heaps read as five copies of one prop rather than as a
  // place something happened to.
  ruin: 3,
  // One per family of work rather than one per job kind: a pick, an axe, a
  // sheaf, a bench, a watch post and a spade, because five of the eleven kinds
  // are *at a bench* and a forge that looked different from a carpenter's would
  // be the client claiming to know something about buildings it reads off a
  // lot's own row instead (`witness/scenes.ts` maps the eleven onto these six).
  toil: 6,
  // One per family of material: rock, timber, growing things, and made things.
  goods: 4,
  // One: a body is a body, and two colourways would be the client deciding
  // something about the dead it has no read for.
  body: 1,
  // One: a town has one library and towns are never side by side, so a second
  // colourway would never share a screenshot with the first (the town board's
  // reasoning).
  shelf: 1,
};

/** Where a screenshot of one kind of prop wants to be taken from. */
export interface PropVantage {
  /** How far in front of the prop to stand, in tiles. */
  readonly distance: number;

  /** How high above the prop's feet to aim, in tiles. */
  readonly height: number;

  /**
   * Which side of the prop is its front, as a multiplier on its local +y.
   *
   * `+1` for a walker, whose geometry faces the way it is going, and `-1` for a
   * monument, whose face is turned towards whoever walks up to read it (see
   * {@link boxesFor}). Getting this wrong photographs the backs of people's
   * heads, which is how it came to be written down.
   */
  readonly side: number;

  /**
   * How far round the prop from its front to stand, in radians.
   *
   * Zero is straight in front, which is right for the two props that are *read*
   * — a board and a headstone are flat faces and a three-quarter view of one is
   * a view of an edge. Everything else is photographed off the shoulder, for two
   * reasons that happen to agree: a box lit by one sun shows its form when two
   * faces are visible, and **a walker walks into a camera parked in front of
   * it**. A body ambles at 1.4 tiles a second and a refugee column at 0.7, so a
   * few frames between posing the camera and taking the picture is a subject in
   * the lens — off the shoulder they pass by instead.
   */
  readonly bearing: number;
}

/**
 * Where each kind wants to be looked at from.
 *
 * Distance is roughly "close enough to read, far enough to see the silhouette",
 * which is a different number for a knee-high stone and for a two-and-a-half
 * tile ruin; height is the middle of the thing worth seeing — a face, the
 * notices on a board, the carved face of a stone.
 *
 * **Every distance to a prop the verb can address is inside
 * `INTERACT_RANGE_TILES`** (`witness/interact.ts`, three tiles), with a tile of
 * slack for the height difference a terrace puts between a vantage and the prop
 * it looks at. That is a constraint rather than a coincidence: the whole point
 * of a vantage is that a driver can stand there, photograph the thing, and then
 * press F on it, and a photographer's distance that put the subject out of arm's
 * reach would need a second position and a second thing to keep in step. The
 * wide-angle 75-degree camera (`render/scene.ts`) is what makes the two
 * compatible — a 2.7-tile stand-off frames four tiles of height.
 *
 * The exception is the one prop that answers to nothing: `smoke` is fifteen
 * tiles tall, stands over another settlement, and is photographed from the town
 * that can see it. Nothing is pressed there, so nothing has to be in reach.
 *
 * The only consumer is the dev handle's screenshot vantage
 * (`witness/witness.ts`, `dev/handle.ts`) and it lives here because how big a
 * prop is, and which way round it is, is this module's business and nobody
 * else's.
 */
const VANTAGE: Readonly<Record<PropKind, PropVantage>> = {
  npc: { distance: 2.7, height: 1.35, side: 1, bearing: 0.8 },
  grave: { distance: 1.9, height: 0.6, side: -1, bearing: 0.12 },
  "market-board": { distance: 2.3, height: 1.2, side: -1, bearing: 0 },
  "burned-farmstead": { distance: 2.7, height: 1.15, side: -1, bearing: 0.5 },
  battlefield: { distance: 2.5, height: 0.85, side: -1, bearing: 0.6 },
  refugees: { distance: 2.6, height: 1.2, side: 1, bearing: 1 },
  // A signpost is read from the town side — its arm points *away*, down the road
  // (see {@link signpostBoxes}) — and from well off the axis, because an arm
  // photographed from behind the post it hangs on is a post. Judged on a
  // screenshot at 0.45 radians, where it was exactly that.
  signpost: { distance: 2.4, height: 1.5, side: -1, bearing: 1.15 },
  // Read the way the market board is — from in front, a little off the axis so
  // the posts give the plank its depth.
  "town-board": { distance: 2.6, height: 1.45, side: -1, bearing: 0.4 },
  // **The one vantage in the file that is not inside arm's reach**, and
  // deliberately: smoke over a sacked town is a thing seen from the town that is
  // watching it, and a photograph taken from under it is a photograph of a grey
  // wall. Nothing can be pressed here, which is why the constraint the rest of
  // the table is held to does not apply — see {@link PropVantage}.
  smoke: { distance: 30, height: 9, side: -1, bearing: 0 },
  // Read the way a headstone is: a book lies flat on a plinth and is looked
  // *down* at, so the camera is close, low and barely off the axis — from the
  // side the pages are two edges.
  chronicle: { distance: 1.9, height: 0.78, side: -1, bearing: 0.15 },
  // Photographed off the shoulder like the burned farmstead it is built out of:
  // a heap of char lit by one sun shows its form when two faces are visible,
  // and dead-on it is a dark patch.
  ruin: { distance: 2.6, height: 0.9, side: -1, bearing: 0.55 },
  // A tool leaning at a work site: photographed from the side, because a shaft
  // pointed at the camera is a dot, and from the height its head is at.
  toil: { distance: 2.4, height: 1.0, side: -1, bearing: 0.9 },
  // A heap on the ground is looked *down* at, so the camera is close and low —
  // the chronicle's arrangement for the same reason.
  goods: { distance: 1.7, height: 0.35, side: -1, bearing: 0.4 },
  // A body is looked down at from the head end and a little off the axis, which
  // is where its length reads as a length rather than as a dot.
  body: { distance: 2.0, height: 0.9, side: 1, bearing: 0.45 },
  // Read the way the market board is: from in front, a little off the axis so
  // the uprights give the shelves their depth.
  shelf: { distance: 2.2, height: 1.0, side: -1, bearing: 0.3 },
};

/** One box of a prop: a size, a place, a tilt and a colour. */
interface Box {
  /** Extent along x, y and z, in tiles. */
  readonly size: readonly [number, number, number];

  /** Centre, in tiles, relative to the prop's feet. */
  readonly at: readonly [number, number, number];

  /** sRGB, converted to the renderer's linear working space on the way in. */
  readonly color: number;

  /** Rotation about x, y and z in radians, applied before the translation. */
  readonly tilt?: readonly [number, number, number];
}

/** A prop in the scene, and enough of its description to know when it must be rebuilt. */
interface Placed {
  readonly mesh: THREE.Mesh;
  readonly kind: PropKind;
  readonly palette: number;

  /**
   * The stage its geometry was built at ({@link SceneProp.stage}).
   *
   * Here because it is part of the *shape* rather than part of the placement: a
   * child comes of age on a day boundary and the simulation reconciles the
   * roster there, so the same figure — the same id, the same tunic — is
   * suddenly a different body. Without this, {@link PropRegistry.set} would take
   * its move branch and glide a small body into a large one's position while it
   * stayed small.
   */
  readonly stage: BodyStage;

  /**
   * The scale its geometry was built at ({@link SceneProp.scale}).
   *
   * Beside {@link Placed.stage} and for its reason: a birthday changes the
   * shape rather than the placement, and a rebuild that compared only the word
   * would keep a stale set of boxes the day the stage table is retuned.
   */
  readonly scale: number;

  label: string | null;
  sprite: THREE.Sprite | null;

  /** The sync pass that last claimed it — see {@link PropRegistry.sync}. */
  generation: number;
}

export class PropRegistry {
  private readonly scene: THREE.Scene;
  private readonly material: THREE.Material;
  private readonly placed = new Map<string, Placed>();

  /** Geometry by `kind:palette`. Shared across every prop that wants it. */
  private readonly geometries = new Map<string, THREE.BufferGeometry>();

  /** Label textures by their text, so a repeated name costs one canvas. */
  private readonly labels = new Map<string, THREE.Texture>();

  /** Bumped by every {@link PropRegistry.sync}; stale props are swept after it. */
  private generation = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // The same material the terrain uses and for the same reason: colour comes
    // out of the vertex stream, so there is nothing per-prop to vary and one
    // material keeps the draws batchable (`render/chunkMeshes.ts`).
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
  }

  /** How many props are in the scene. */
  get size(): number {
    return this.placed.size;
  }

  /** How many draw calls they cost before culling: a mesh each, plus a label each. */
  get drawObjects(): number {
    let count = 0;

    for (const entry of this.placed.values()) {
      count += entry.sprite === null ? 1 : 2;
    }

    return count;
  }

  /**
   * Makes the scene match a list of props exactly.
   *
   * Mark and sweep against a generation counter rather than a set of the ids
   * seen: this runs every frame, the list is a few dozen entries, and a counter
   * costs no allocation where a `Set` costs one per frame forever.
   */
  sync(props: readonly SceneProp[]): void {
    this.generation += 1;

    for (const prop of props) {
      this.set(prop);
    }

    for (const [id, entry] of this.placed) {
      if (entry.generation !== this.generation) {
        this.discard(entry);
        this.placed.delete(id);
      }
    }
  }

  /**
   * Adds a prop, or moves and relabels one that is already there.
   *
   * Moving is the common case by a wide margin — a body takes a step thirty
   * times a second and never changes shape — so the geometry is only rebuilt
   * when the kind, the colourway or the life stage actually differs.
   *
   * The stage is in that identity for one case that would otherwise be
   * invisible: coming of age (see {@link Placed.stage}).
   */
  set(prop: SceneProp): void {
    const existing = this.placed.get(prop.id);
    // A prop that named no stage is a thing rather than a body
    // ({@link SceneProp.stage}), and a thing is drawn grown at full size.
    const stage = prop.stage ?? "adult";
    const scale = prop.scale ?? FULL_SCALE_PERMILLE;

    if (existing !== undefined) {
      if (
        existing.kind === prop.kind &&
        existing.palette === prop.palette &&
        existing.stage === stage &&
        existing.scale === scale
      ) {
        this.position(existing.mesh, prop);
        this.relabel(existing, prop.label);
        existing.generation = this.generation;

        return;
      }

      // A different shape under the same id: the geometry cannot be reused, so
      // the prop is replaced rather than left drawing what it used to be.
      this.discard(existing);
      this.placed.delete(prop.id);
    }

    const mesh = new THREE.Mesh(
      this.geometryFor(prop.kind, prop.palette, stage, scale),
      this.material,
    );
    mesh.name = `prop ${prop.id}`;
    // The geometry is built around the prop's own feet, so the object's matrix
    // is the whole of its placement and it is written on a move rather than
    // recomputed every frame (`render/highlight.ts`).
    mesh.matrixAutoUpdate = false;
    this.position(mesh, prop);

    const entry: Placed = {
      mesh,
      kind: prop.kind,
      palette: prop.palette,
      stage,
      scale,
      label: null,
      sprite: null,
      generation: this.generation,
    };
    this.relabel(entry, prop.label);
    this.placed.set(prop.id, entry);
    this.scene.add(mesh);
  }

  /** Drops one prop. Silent if there was none. */
  remove(id: string): void {
    const entry = this.placed.get(id);

    if (entry === undefined) {
      return;
    }

    this.discard(entry);
    this.placed.delete(id);
  }

  /** Drops every prop. The shared geometries and textures outlive it. */
  clear(): void {
    for (const entry of this.placed.values()) {
      this.discard(entry);
    }

    this.placed.clear();
  }

  /** Releases the shared geometry, textures and material too. Unusable afterwards. */
  dispose(): void {
    this.clear();

    for (const geometry of this.geometries.values()) {
      geometry.dispose();
    }

    for (const texture of this.labels.values()) {
      texture.dispose();
    }

    this.geometries.clear();
    this.labels.clear();
    this.material.dispose();
  }

  /** Writes a prop's placement into its mesh matrix. */
  private position(mesh: THREE.Mesh, prop: SceneProp): void {
    mesh.position.set(prop.x, prop.y, prop.z);
    // About world +z, because that is up (`render/scene.ts` teaches the whole
    // scene the simulation's frame). Rotating by the yaw takes a geometry built
    // facing +y to a geometry facing wherever the prop is turned, which is the
    // same mapping `controller.ts` applies to a movement intent.
    mesh.rotation.z = prop.facing;
    mesh.updateMatrix();
  }

  /** Attaches, replaces or removes a prop's floating name. */
  private relabel(entry: Placed, label: string | null): void {
    if (entry.label === label) {
      return;
    }

    if (entry.sprite !== null) {
      entry.mesh.remove(entry.sprite);
      entry.sprite.material.dispose();
      entry.sprite = null;
    }

    entry.label = label;

    if (label === null) {
      return;
    }

    const texture = this.labelTexture(label);
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        // A label writes no depth so two of them overlapping blend instead of
        // punching a hole in each other, and tests depth so one behind a hill
        // stays behind it.
        depthWrite: false,
      }),
    );

    // The plate is as wide as the text made it, so the sprite has to be too;
    // a fixed square would squash a long name and pad a short one. Past
    // {@link LABEL_MAX_WIDTH_TILES} the whole sprite shrinks instead of growing
    // sideways, so the aspect is preserved and the plate stops taking the frame.
    const canvas: unknown = texture.image;
    const aspect = canvas instanceof HTMLCanvasElement ? canvas.width / canvas.height : 1;
    const height = Math.min(LABEL_SIZE_TILES, LABEL_MAX_WIDTH_TILES / Math.max(aspect, 1));
    sprite.scale.set(height * aspect, height, 1);
    // A child of the body, so one matrix write moves both. Sprites face the
    // camera by construction, which is the whole reason a label is one.
    //
    // **At the height of the head it is over** ({@link plateHeightFor}, chunk
    // Fx-7), which is why the entry carries the scale it was built at: a body
    // that changed size was rebuilt rather than moved ({@link PropRegistry.set}),
    // so the plate is re-hung with the geometry rather than left where the
    // grown one wanted it.
    sprite.position.set(0, 0, plateHeightFor(entry.scale));
    entry.mesh.add(sprite);
    entry.sprite = sprite;
  }

  /** Removes a prop from the scene. Shared geometry and textures are untouched. */
  private discard(entry: Placed): void {
    if (entry.sprite !== null) {
      entry.mesh.remove(entry.sprite);
      entry.sprite.material.dispose();
      entry.sprite = null;
    }

    this.scene.remove(entry.mesh);
  }

  /** The shared geometry for one kind in one colourway at one size, built on first use. */
  private geometryFor(
    kind: PropKind,
    palette: number,
    stage: BodyStage,
    scale: number,
  ): THREE.BufferGeometry {
    // Modulo the kind's own variant count, and floored into range from below as
    // well as above: a caller may hand this a feature id, and nothing promises
    // one of those is positive.
    const count = VARIANT_COUNT[kind];
    const index = ((palette % count) + count) % count;
    // The stage is in the key rather than in a second map, because it is part of
    // what the boxes are: a child's body is a different geometry, shared by every
    // child in the same tunic exactly as an adult's is (the header's note on
    // geometry being shared and meshes not).
    const key = `${kind}:${index}:${stage}:${scale}`;
    const cached = this.geometries.get(key);

    if (cached !== undefined) {
      return cached;
    }

    const geometry = buildGeometry(boxesFor(kind, index, scale));
    this.geometries.set(key, geometry);

    return geometry;
  }

  /** The shared texture for one label's text, drawn on first use. */
  private labelTexture(text: string): THREE.Texture {
    const cached = this.labels.get(text);

    if (cached !== undefined) {
      return cached;
    }

    const texture = drawLabel(text);
    this.labels.set(text, texture);

    return texture;
  }
}

/**
 * Returns the prop a feature is drawn as.
 *
 * The one place the client turns the simulation's feature ids into geometry. An
 * unknown id draws as a battlefield cairn rather than throwing: the ids are
 * PROVISIONAL until wave 4A checks them against `sim-core` (`world/types.ts`),
 * and a stone in the wrong place is a better failure than a blank screen while
 * that check does not exist yet.
 */
export function propKindForFeature(feature: AftermathFeature): PropKind {
  switch (feature.kind) {
    case FEATURE_GRAVE:
      return "grave";
    case FEATURE_BURNED_FARMSTEAD:
      return "burned-farmstead";
    case FEATURE_MARKET_BOARD:
      return "market-board";
    case FEATURE_BATTLEFIELD:
    default:
      return "battlefield";
  }
}

/**
 * Returns where a camera should stand to photograph one kind of prop.
 *
 * The dev handle's screenshot vantage asks (`witness/witness.ts`); nothing in a
 * play session does. See {@link VANTAGE}.
 *
 * `scale` is read by the one kind that has a size, and only for the aim: the
 * table aims at *the middle of the thing worth seeing*, and an adult's 1.35
 * tiles is half a tile over a child's head, so a portrait of a child was a
 * photograph of the ground in front of one. The stand-off is left alone
 * deliberately — a smaller subject at the same distance is a looser shot of the
 * same street, which is what a picture of a child *beside* a grown neighbour is
 * for.
 *
 * **The number comes off the body plan since wave 2A** ({@link SceneProp.scale}
 * — the simulation's own size curve), which is what the two authored child
 * constants here were a stub for.
 */
export function propVantage(
  kind: PropKind,
  scale: number = FULL_SCALE_PERMILLE,
): PropVantage {
  const vantage = VANTAGE[kind];

  if (kind !== "npc" || scale >= FULL_SCALE_PERMILLE) {
    return vantage;
  }

  return { ...vantage, height: (vantage.height * scale) / FULL_SCALE_PERMILLE };
}

/**
 * The boxes one kind of prop is built out of, facing +y, feet at the origin.
 *
 * Scale is the first thing these have to get right and the hardest to fix
 * later, so it is stated once here: **a tile is a metre and the player's eye is
 * at 1.62 of them** (`player/controller.ts`). A body is a little shorter than
 * the player, a headstone comes to the knee, a board is read at eye height, and
 * the chimney of a burned farmstead is the only thing in the file taller than a
 * person — which is exactly why it is the shape that carries that prop.
 *
 * Facing is the other convention worth stating, because the two halves of it
 * differ: a *walker* (a body, a refugee column) faces the way it moves, so its
 * front is local +y; a *monument* (a board, a headstone) is read from in front
 * of it, so its face is local -y and it is placed with a facing that turns that
 * face towards whoever walks up (`world/fixture.ts`'s layout).
 *
 * `scale` is read by the one kind that is a person ({@link SceneProp.scale}).
 * Everything else is a thing and gets the same boxes at any size.
 */
function boxesFor(kind: PropKind, variant: number, scale: number): readonly Box[] {
  switch (kind) {
    case "npc":
      return npcBoxes(NPC_TUNICS[variant] ?? NPC_TUNICS[0] ?? PALETTE.clothPale, scale);
    case "grave":
      return graveBoxes(variant);
    case "market-board":
      return marketBoardBoxes();
    case "burned-farmstead":
      return burnedFarmsteadBoxes();
    case "battlefield":
      return battlefieldBoxes();
    case "refugees":
      return refugeeBoxes(variant);
    case "signpost":
      return signpostBoxes();

    case "town-board":
      return townBoardBoxes();
    case "smoke":
      return smokeBoxes();
    case "chronicle":
      return chronicleBoxes(variant);
    case "ruin":
      return ruinBoxes(variant);
    case "toil":
      return toilBoxes(variant);
    case "goods":
      return goodsBoxes(variant);
    case "body":
      return bodyBoxes();
    case "shelf":
      return shelfBoxes();
  }
}

/**
 * Where an adult's shoulders stop and its head starts, in tiles.
 *
 * The tunic's top edge, written down rather than left implicit in two box rows,
 * because the child below is built by scaling *up to* it and setting a head on
 * top: two numbers that had to agree by hand were a body with a floating head
 * the first time either of them moved.
 */
const NPC_NECK_TILES = 1.32;

/** How wide an adult's head is, in tiles. A cube, so this is its height too. */
const NPC_HEAD_TILES = 0.26;

/**
 * How wide a grown body is drawn, in tiles: the tunic's own span, which is the
 * widest box in one.
 *
 * Exported for {@link LABEL_HEIGHT_TILES}'s reason — a second reader that must
 * not be allowed to pick its own number. `witness/scenes.ts` spreads two bodies
 * sharing one tile by it (chunk Fx-7), and a spread that guessed at a width
 * would either draw them through each other or push one of them out of the tile
 * every verb cites.
 */
export const NPC_WIDTH_TILES = 0.44;

/**
 * How much clear air a plate keeps over the head it floats above, in tiles.
 *
 * **Derived rather than picked**: it is what {@link LABEL_HEIGHT_TILES} leaves
 * over a grown body's own head, so the gap a screenshot was judged on is the gap
 * every body keeps whatever size the simulation draws it at
 * ({@link plateHeightFor}).
 */
const LABEL_CLEARANCE_TILES = LABEL_HEIGHT_TILES - (NPC_NECK_TILES + NPC_HEAD_TILES);

/**
 * What a body drawn at its full grown size measures, in the units the size
 * curve is exported in: 1,000 per mille.
 *
 * Not a taste number and not a scale — it is the denominator of the
 * simulation's own per-mille ({@link SceneProp.scale},
 * `sim_core::body::LifeStage::scale_permille`), written down once so the three
 * places that divide by it cannot disagree.
 */
const FULL_SCALE_PERMILLE = 1_000;

/**
 * How much of a body's shrink the head keeps: 0.42 as an exponent.
 *
 * **The one authored rule about a body's *proportions* left on this client, and
 * it is a drawing rule rather than a fact about a person.** The simulation owns
 * how *tall* a body is — the size curve is body-plan data and crosses the wall
 * as one number per stage ({@link SceneProp.scale}) — and what is left here is
 * head-to-body ratio, which is the thing that makes a small silhouette read as a
 * child rather than as an adult standing further away. §8.2 of the data-model
 * study is the line this is drawn on: *"consider separating physics numbers from
 * tuning numbers"*. A body's volume is physics and lives in `sim_core::body`;
 * how a renderer makes four boxes read as a nine-year-old is tuning and lives
 * here.
 *
 * Applied as `headScale = bodyScale ** HEAD_SHRINK_EXPONENT`, so a head always
 * shrinks *less* than the body it sits on and never more: an adult at 1.0 keeps
 * its head, the shipped child at 0.753 keeps 0.887 of it, the toddler at 0.598
 * keeps 0.806, and the baby at 0.440 keeps 0.709 — which is a big-headed infant,
 * which is what an infant is. The exponent is the one that reproduces the pair
 * the wave-1.6 playtest was judged on (a body at 0.55 with a head at 0.78),
 * carried onto a curve instead of onto a boolean.
 *
 * PROVISIONAL, and judged on a screenshot beside an adult like every other
 * aesthetic constant in this file.
 */
const HEAD_SHRINK_EXPONENT = 0.42;

/**
 * How high a plate floats over a body drawn at `scale`, in tiles.
 *
 * **The plate follows the head it is over** (chunk Fx-7).
 * {@link LABEL_HEIGHT_TILES} is one number for everybody and it was written when
 * everybody was one size, so since wave 2A put a life-stage curve under the
 * bodies a child's name has floated a third of a tile above their head — the
 * same defect the *body* had before that wave, one element up.
 *
 * The rule is the geometry's own and not a second opinion about it: a plate sits
 * {@link LABEL_CLEARANCE_TILES} over wherever the head this body was built with
 * actually ends, which is {@link npcBoxes}' two numbers scaled exactly as it
 * scales them — the neck by the simulation's per-mille, the head by rather less
 * ({@link HEAD_SHRINK_EXPONENT}). So a retune of the size curve or of the head
 * ratio moves the plate with the body and nothing here has an opinion about it.
 *
 * A grown body — and every prop that is a thing rather than a person, since one
 * of those names no scale at all ({@link SceneProp.scale}) — is
 * {@link LABEL_HEIGHT_TILES} to the digit, so nothing that was drawn before this
 * existed moved.
 */
export function plateHeightFor(scale: number): number {
  const height = Math.max(scale, 0) / FULL_SCALE_PERMILLE;

  if (height >= 1) {
    return LABEL_HEIGHT_TILES;
  }

  return (
    NPC_NECK_TILES * height +
    NPC_HEAD_TILES * Math.pow(height, HEAD_SHRINK_EXPONENT) +
    LABEL_CLEARANCE_TILES
  );
}

/**
 * A body: legs, torso, two arms and a head. Five boxes, sixty triangles.
 *
 * **One box list at every size**, scaled by the number the simulation gave it
 * ({@link SceneProp.scale}), so a baby and a grown smith can never drift into
 * being different people: an art pass that widens a tunic widens it for
 * everybody. That was already this function's rule when there were two stages;
 * what wave 2A changed is that the number comes off the body plan rather than
 * out of a constant beside it.
 */
function npcBoxes(tunic: number, scale: number): readonly Box[] {
  const sleeve = shade(tunic, 0.86);
  // Everything below the neck, kept apart from the head because the two are
  // scaled by different numbers ({@link HEAD_SHRINK_EXPONENT}).
  const body: readonly Box[] = [
    { size: [0.34, 0.24, 0.72], at: [0, 0, 0.36], color: PALETTE.trouser },
    { size: [NPC_WIDTH_TILES, 0.28, 0.6], at: [0, 0, 1.02], color: tunic },
    { size: [0.11, 0.2, 0.52], at: [-0.275, 0, 1.0], color: sleeve },
    { size: [0.11, 0.2, 0.52], at: [0.275, 0, 1.0], color: sleeve },
  ];
  const height = Math.max(scale, 0) / FULL_SCALE_PERMILLE;

  if (height >= 1) {
    return [...body, headBox(NPC_NECK_TILES, NPC_HEAD_TILES)];
  }

  const head = Math.pow(height, HEAD_SHRINK_EXPONENT);

  return [
    ...body.map((box) => shrink(box, height)),
    headBox(NPC_NECK_TILES * height, NPC_HEAD_TILES * head),
  ];
}

/** The head, sitting on the shoulders wherever they ended up. */
function headBox(neck: number, size: number): Box {
  return { size: [size, size, size], at: [0, 0, neck + size / 2], color: PALETTE.skinFair };
}

/**
 * One box of a body at a fraction of its grown size.
 *
 * **The feet stay on the ground.** A box's `at` is its centre, so scaling the
 * size and the centre by the same number scales the box's whole span *from the
 * origin* — and the origin is the prop's feet ({@link boxesFor}). Scaling the
 * size alone would leave a child's boots hanging half a tile in the air.
 */
function shrink(box: Box, by: number): Box {
  return {
    size: [box.size[0] * by, box.size[1] * by, box.size[2] * by],
    at: [box.at[0] * by, box.at[1] * by, box.at[2] * by],
    color: box.color,
    // Spread rather than assigned, because `tilt` is optional and the checker is
    // strict about the difference between absent and `undefined` (`tsconfig.json`).
    // No body box tilts today; a later one that did would keep its angle, which
    // is what scaling means.
    ...(box.tilt === undefined ? {} : { tilt: box.tilt }),
  };
}

/**
 * A named burial: a mound, a plinth and a stone with a shoulder on it.
 *
 * The mound is what makes it a grave rather than a signpost, and it is on the
 * stone's *front* (-y) so that a player who has walked up to read the
 * inscription is standing at the foot of it. Knee-to-waist height, because the
 * minimal `state_profilest` burial model (history §6.2) is a slab and a name and
 * this phase has no masonry to spend on anything grander.
 *
 * Two variants, cosmetic only (see {@link VARIANT_COUNT}): one cut recently,
 * with pale stone standing square over turned earth, and one that has been out
 * in the weather long enough to lean and to grow moss over.
 */
function graveBoxes(variant: number): readonly Box[] {
  const weathered = variant % 2 === 1;
  // A small lean, and a small one on purpose: a stone at five degrees reads as
  // old, and one at fifteen reads as knocked over by something.
  const lean = weathered ? 0.09 : 0;
  const stone = weathered ? PALETTE.stoneShade : PALETTE.stoneFace;
  const cap = weathered ? PALETTE.stoneBase : PALETTE.stonePale;

  return [
    { size: [0.66, 0.44, 0.1], at: [0, 0.02, 0.05], color: PALETTE.stoneBase },
    // The mound. Low and no wider than the plinth, because a tall one reads as a
    // crate and a wide one as a lawn — both of which it did, at the sizes this
    // started at.
    {
      size: [0.54, 0.78, 0.11],
      at: [0, -0.5, 0.055],
      color: weathered ? PALETTE.moss : PALETTE.earthFresh,
    },
    { size: [0.44, 0.13, 0.68], at: [0, 0, 0.44], color: stone, tilt: [lean, 0, 0] },
    // The shoulder, which is the whole of the silhouette's argument that this is
    // a carved stone and not a fence post.
    { size: [0.52, 0.17, 0.11], at: [0, -0.03, 0.82], color: cap, tilt: [lean, 0, 0] },
  ];
}

/**
 * The market board: two posts, a planed face, a shelf and a little pitched roof.
 *
 * The roof is doing the work. A board without one is two posts and a plank,
 * which at any distance is a fence; a roof turns the same silhouette into
 * something built to keep paper dry, and paper is the point — the notices are
 * the brightest thing in the whole prop set, because the board is the one prop a
 * player is expected to look for.
 */
function marketBoardBoxes(): readonly Box[] {
  return [
    { size: [0.1, 0.1, 1.32], at: [-0.56, 0, 0.66], color: PALETTE.timberDark },
    { size: [0.1, 0.1, 1.32], at: [0.56, 0, 0.66], color: PALETTE.timberDark },
    { size: [1.3, 0.09, 0.7], at: [0, 0, 1.16], color: PALETTE.timberFace },
    // A shelf under the notices, out of the same timber as the posts: it catches
    // the light differently from the face above it and stops the board reading
    // as one flat slab.
    { size: [1.36, 0.18, 0.06], at: [0, -0.05, 0.78], color: PALETTE.timberDark },
    // The roof, two planks leaned against each other over the face. The signs
    // are the way round they are because `rotateY` lifts a plank's -x end: the
    // left plank has to lean the *other* way from the obvious one, or the two of
    // them make a valley instead of a ridge. Judged on a screenshot, which is
    // where the valley was found.
    { size: [0.78, 0.44, 0.05], at: [-0.32, 0, 1.62], color: PALETTE.timberDark, tilt: [0, -0.4, 0] },
    { size: [0.78, 0.44, 0.05], at: [0.32, 0, 1.62], color: PALETTE.timberDark, tilt: [0, 0.4, 0] },
    // The notices themselves, proud of the board's face so they catch the sun
    // differently. Two prices and a proclamation, in the abstract.
    { size: [0.34, 0.02, 0.4], at: [-0.4, -0.055, 1.2], color: PALETTE.paperPale },
    { size: [0.28, 0.02, 0.3], at: [0.0, -0.055, 1.1], color: PALETTE.paperMid },
    { size: [0.24, 0.02, 0.34], at: [0.38, -0.055, 1.24], color: PALETTE.paperWorn },
  ];
}

/**
 * A farmstead that burned: a scorched footprint, two sills, three fallen
 * rafters, and the chimney that outlived all of it.
 *
 * The chimney is the silhouette. Everything a fire leaves is at ground level and
 * therefore invisible from more than a few tiles away — a burned building reads
 * from a distance as *one standing stack over a dark patch*, which is what this
 * builds, and which is why the stack is the tallest thing in the file.
 *
 * `WG_RUINED`-as-a-flag is the precedent (buildings §10.2): the simulation says
 * a site is ruined and the client draws the ruin. Nothing here collides and
 * nothing here is a tile (plan decision 6).
 */
function burnedFarmsteadBoxes(): readonly Box[] {
  return [
    { size: [2.6, 2.2, 0.1], at: [0, 0, 0.05], color: PALETTE.charMid },
    { size: [2.6, 0.24, 0.34], at: [0, -0.98, 0.2], color: PALETTE.charSoot },
    { size: [0.24, 2.2, 0.28], at: [-1.18, 0, 0.17], color: PALETTE.charDeep },
    // The stack, and the cold hearth in the bottom of it. Sooted rather than
    // clean: at the cairn's own grey it read as a monument somebody had built
    // beside a fire rather than as the one part of a house that survived one.
    { size: [0.62, 0.62, 2.05], at: [0.85, 0.72, 1.02], color: PALETTE.stackSoot },
    { size: [0.74, 0.74, 0.14], at: [0.85, 0.72, 2.12], color: PALETTE.charSoot },
    { size: [0.34, 0.12, 0.4], at: [0.85, 0.4, 0.3], color: PALETTE.charDeep },
    // Three rafters that fell inwards and stayed there. Different lengths and
    // different angles, and — the part that only a screenshot tells you — they
    // fall in different *compass* directions: three beams leaning the same way
    // are three beams that all look vertical from the one side you photograph
    // them from.
    {
      size: [0.16, 0.16, 2.2],
      at: [-0.45, 0.3, 0.78],
      color: PALETTE.charDeep,
      tilt: [0.95, 0, 1.15],
    },
    {
      size: [0.14, 0.14, 1.8],
      at: [0.3, -0.25, 0.62],
      color: PALETTE.charMid,
      tilt: [-0.75, 0.25, 0.35],
    },
    {
      size: [0.12, 0.12, 1.4],
      at: [-0.15, 0.8, 0.48],
      color: PALETTE.charSoot,
      tilt: [1.15, 0, -0.9],
    },
    // Ash where the roof came down, and what is still warm under it — the one
    // saturated colour on the prop, and the reason it does not read as rubble.
    { size: [0.9, 0.7, 0.12], at: [-0.5, -0.3, 0.1], color: PALETTE.ashPale },
    { size: [0.5, 0.4, 0.08], at: [0.1, -0.2, 0.15], color: PALETTE.ember },
  ];
}

/**
 * A cairn over a battlefield: four stones stacked crooked, a broken spear, and
 * two kerb stones somebody set out at the edge.
 *
 * Each stone is turned about z by a different amount, which is the whole trick —
 * four axis-aligned boxes read as a ziggurat and four turned ones read as
 * something stacked by hand. The spear says what the stones are for; without it
 * the prop is a pile of rocks, and a pile of rocks is what a player walks past.
 */
function battlefieldBoxes(): readonly Box[] {
  // The lean the shaft and its head share, so the head sits on the end of the
  // shaft rather than near it: the direction of a box's local +z after these two
  // rotations is (-0.329, -0.057, 0.943), which puts the head of a 1.55-long
  // shaft centred at (0.34, 0.26, 1.0) at about (0.08, 0.21, 1.75).
  //
  // The second rotation is what turns the lean sideways. A shaft that leans only
  // about x leans towards or away from whoever is looking at the cairn from the
  // front, and a leaning stick pointed at the camera is a vertical stick.
  const shaftTilt: readonly [number, number, number] = [0.34, 0, -1.4];

  return [
    { size: [1.05, 0.95, 0.3], at: [0, 0, 0.15], color: PALETTE.cairnDark, tilt: [0, 0, 0.25] },
    { size: [0.8, 0.74, 0.26], at: [0.05, -0.04, 0.42], color: PALETTE.cairnMid, tilt: [0, 0, -0.5] },
    { size: [0.58, 0.54, 0.24], at: [-0.04, 0.05, 0.66], color: PALETTE.cairnLit, tilt: [0, 0, 0.9] },
    {
      size: [0.36, 0.34, 0.2],
      at: [0.02, -0.02, 0.86],
      color: PALETTE.cairnPale,
      tilt: [0, 0, -0.2],
    },
    { size: [0.07, 0.07, 1.55], at: [0.34, 0.26, 1.0], color: PALETTE.timberDark, tilt: shaftTilt },
    { size: [0.09, 0.04, 0.26], at: [0.08, 0.21, 1.75], color: PALETTE.ironDull, tilt: shaftTilt },
    // A second shaft, snapped and lying where it fell.
    {
      size: [0.06, 0.06, 0.9],
      at: [-0.42, -0.3, 0.12],
      color: PALETTE.timberDark,
      tilt: [1.35, 0, 0.4],
    },
    { size: [0.28, 0.26, 0.14], at: [-0.72, 0.42, 0.07], color: PALETTE.cairnMid },
    { size: [0.22, 0.24, 0.12], at: [0.62, -0.55, 0.06], color: PALETTE.cairnDark },
  ];
}

/**
 * A knot of people on the road, drawn as a group and never as individuals.
 *
 * History §8.4's refugee check moves populations rather than people, and the
 * plan keeps that granularity all the way to the screen (decision 8): this is
 * one prop with one label saying how many, not four bodies with four names the
 * simulation does not have. Four figures is enough for a cluster to read as a
 * cluster and few enough that it never reads as a countable roster — the number
 * that matters is on the label, and the label comes from the record.
 *
 * Built out of the same legs/torso/head vocabulary a townsman is
 * ({@link npcBoxes}), because that is what makes them read as *people* at all:
 * a single tall box per figure, which is where this started, reads as a fence.
 *
 * What separates them from the townsfolk twenty tiles behind them, at a glance:
 * they are **carrying things**, they are **in a column rather than a ring**, and
 * one of them is a child. The blanket roll is the group's one accent colour.
 */
function refugeeBoxes(variant: number): readonly Box[] {
  const second = variant % 2 === 1;
  const cloth = second ? PALETTE.clothDim : PALETTE.clothWorn;
  const clothOther = second ? PALETTE.clothWorn : PALETTE.clothDust;

  return [
    // Walking at the back, under a pack. A pack goes at -y: a walker's front is
    // +y, so that is their back.
    { size: [0.3, 0.22, 0.66], at: [-0.42, 0.1, 0.33], color: PALETTE.trouser },
    { size: [0.38, 0.26, 0.56], at: [-0.42, 0.1, 0.94], color: cloth },
    { size: [0.22, 0.22, 0.24], at: [-0.42, 0.1, 1.36], color: PALETTE.skinFair },
    { size: [0.32, 0.24, 0.36], at: [-0.42, -0.2, 0.98], color: PALETTE.bundle },

    // Leaning on a staff.
    { size: [0.28, 0.2, 0.62], at: [0.06, -0.18, 0.31], color: PALETTE.trouser },
    { size: [0.36, 0.24, 0.52], at: [0.06, -0.18, 0.88], color: clothOther },
    { size: [0.21, 0.21, 0.23], at: [0.06, -0.18, 1.28], color: PALETTE.skinDark },
    { size: [0.05, 0.05, 1.5], at: [0.26, -0.12, 0.74], color: PALETTE.timberDark, tilt: [0.1, 0, 0.08] },

    // A child, keeping up. The one shape in the group that says *families* and
    // not *soldiers*, so it is worth the three boxes.
    { size: [0.18, 0.14, 0.4], at: [0.44, 0.16, 0.2], color: PALETTE.trouser },
    { size: [0.24, 0.18, 0.36], at: [0.44, 0.16, 0.58], color: PALETTE.clothPale },
    { size: [0.18, 0.18, 0.19], at: [0.44, 0.16, 0.86], color: PALETTE.skinFair },

    // Out in front, with the bedding across their shoulders.
    { size: [0.28, 0.2, 0.6], at: [-0.02, 0.44, 0.3], color: PALETTE.trouser },
    { size: [0.36, 0.24, 0.5], at: [-0.02, 0.44, 0.85], color: PALETTE.clothDust },
    { size: [0.2, 0.2, 0.22], at: [-0.02, 0.44, 1.22], color: PALETTE.skinDark },
    { size: [0.46, 0.17, 0.17], at: [-0.02, 0.44, 1.15], color: PALETTE.blanket, tilt: [0, 0, 0.12] },
  ];
}

/**
 * A fingerpost at a road mouth: two set stones, a post, and one arm pointing
 * down the road it names.
 *
 * **The arm points along local +y, which is away from the town**, and the prop
 * is placed with the facing of the road leaving the settlement
 * (`witness/witness.ts`). So a player standing in town sees the arm pointing
 * away from them down a path they can see, which is the whole of Phase 4
 * decision 4's wayfinding: no compass, no marker, a real road and a board with a
 * real settlement's name on it.
 *
 * Where the name actually is, is the label sprite over the post — the same
 * floating plate every other named prop in this file carries. That is a
 * deliberate reuse rather than a shortcut: an arm is legible from beside the
 * road and edge-on from along it, and a name that vanished when the player
 * walked up the road would be a sign that only worked from where you did not
 * need it. The arm says *which way*; the plate says *where to*.
 *
 * Waist-to-head height, because a signpost that cleared a player's eye line
 * would hide the road it is pointing at.
 */
function signpostBoxes(): readonly Box[] {
  return [
    // Two stones set round the foot, which is what stops the post reading as a
    // stake somebody pushed in: a road sign is a thing a town built.
    { size: [0.62, 0.58, 0.2], at: [0, 0, 0.1], color: PALETTE.cairnMid, tilt: [0, 0, 0.3] },
    { size: [0.34, 0.32, 0.16], at: [0.24, -0.2, 0.08], color: PALETTE.cairnDark, tilt: [0, 0, -0.5] },

    { size: [0.15, 0.15, 1.9], at: [0, 0, 0.95], color: PALETTE.timberDark },
    // A cap, so the top of the post is a made edge rather than a cut one.
    { size: [0.24, 0.24, 0.09], at: [0, 0, 1.94], color: PALETTE.timberFace },

    // The arm. Long along +y, thin across it, and pale enough to read against
    // the hillside behind it — this is the one surface on the prop that the eye
    // is meant to find from twenty tiles away.
    //
    // **Hung below the name plate rather than beside it.** Every labelled prop
    // in this file carries its sprite at `LABEL_HEIGHT_TILES`, and a long
    // settlement name is a plate wider than this arm is long: at the arm's first
    // height the two occupied the same band and the screenshot was of a post
    // with a sign floating over it. The plate says where to; the arm says which
    // way; they need different air.
    { size: [0.11, 1.15, 0.3], at: [0, 0.58, 1.3], color: PALETTE.timberFace },
    // Its point, a smaller box turned about z so the end reads as a chamfer
    // rather than as a stop.
    {
      size: [0.11, 0.28, 0.28],
      at: [0, 1.15, 1.3],
      color: PALETTE.timberFace,
      tilt: [0, 0, 0.78],
    },
    // The peg the arm is hung on, on the town side, so the joint is visible from
    // where a player walks up.
    { size: [0.22, 0.14, 0.16], at: [0, -0.02, 1.3], color: PALETTE.timberDark },
  ];
}

/**
 * The board a town hangs its own name on, standing near the centre.
 *
 * Decision 4's missing half, found in the owner's dry-run: the signposts name
 * where every road *goes* and nothing in the world named where the player
 * already *was* — the one settlement a session most needs the player to be able
 * to place is the one they are standing in. Same answer as the signpost: no
 * compass, no toast, a thing the town built with the simulation's own string
 * floating over it.
 *
 * The carpentry sits between the other two boards on purpose: broader than a
 * fingerpost (it carries a name, not a direction), plainer than the market
 * board (no roof, no notices — nothing about it invites the verb, and it is
 * never offered to one). The plank tops out below `LABEL_HEIGHT_TILES` so the
 * plate and the timber never share a band.
 */
function townBoardBoxes(): readonly Box[] {
  return [
    // A stone at each foot — like the signpost's, what makes it a thing a town
    // raised rather than a stake somebody pushed in.
    { size: [0.5, 0.46, 0.18], at: [-0.72, 0, 0.09], color: PALETTE.cairnMid, tilt: [0, 0, 0.4] },
    { size: [0.5, 0.46, 0.18], at: [0.72, 0, 0.09], color: PALETTE.cairnDark, tilt: [0, 0, -0.3] },

    { size: [0.14, 0.14, 1.72], at: [-0.72, 0, 0.86], color: PALETTE.timberDark },
    { size: [0.14, 0.14, 1.72], at: [0.72, 0, 0.86], color: PALETTE.timberDark },
    // Caps, so the posts end on a made edge (the signpost's reasoning).
    { size: [0.22, 0.22, 0.08], at: [-0.72, 0, 1.76], color: PALETTE.timberFace },
    { size: [0.22, 0.22, 0.08], at: [0.72, 0, 1.76], color: PALETTE.timberFace },

    // The board itself: one broad pale plank, the surface the eye is meant to
    // find, hung high enough to read over a crowd's heads.
    { size: [1.9, 0.1, 0.5], at: [0, 0, 1.42], color: PALETTE.timberFace },
    // The rail it hangs from, in the darker timber so the plank reads as a
    // plank on a frame rather than a floating slab.
    { size: [1.64, 0.16, 0.07], at: [0, 0.04, 1.12], color: PALETTE.timberDark },
  ];
}

/**
 * The smoke over a settlement that was stormed this morning.
 *
 * Phase 4 decision 5 is *reaction, never reenactment*: no battle is played out,
 * because a battle is one record landing on one tick and always will be. What
 * the client draws is what a town a road away can see of it — a column going up
 * where the record says the town is (`witness/staging.ts` places it on the true
 * bearing) — and it is drawn only while the record it comes from is that day's.
 *
 * **The one prop in this file built to be read at thirty tiles.** Everything
 * else is judged at conversation distance and photographed from two and a half
 * tiles; this is judged from across a valley, through the fog it will be half
 * eaten by. So it is fifteen tiles tall, it widens and drifts as it rises so the
 * silhouette is a plume rather than a chimney, and its four values are spread
 * wider than anything else in {@link PALETTE}.
 *
 * Six boxes, because a plume made of more would still be a stack of boxes: what
 * makes it read is the drift, not the count.
 */
function smokeBoxes(): readonly Box[] {
  return [
    { size: [1.5, 1.5, 2.8], at: [0, 0, 1.4], color: PALETTE.smokeSoot },
    { size: [2.0, 1.9, 2.8], at: [0.4, 0.25, 3.9], color: PALETTE.smokeSoot, tilt: [0, 0, 0.35] },
    { size: [2.6, 2.4, 3.0], at: [1.0, 0.6, 6.6], color: PALETTE.smokeMid, tilt: [0, 0, -0.28] },
    { size: [3.3, 3.0, 3.2], at: [1.9, 1.1, 9.5], color: PALETTE.smokeMid, tilt: [0, 0, 0.5] },
    { size: [4.0, 3.6, 3.2], at: [3.0, 1.8, 12.4], color: PALETTE.smokeLit, tilt: [0, 0, -0.4] },
    { size: [4.6, 4.0, 2.8], at: [4.4, 2.6, 15.0], color: PALETTE.smokeHead, tilt: [0, 0, 0.22] },
  ];
}

/**
 * A book left where it can be found: a cut plinth, and the book lying open on
 * it.
 *
 * Phase 5 decision 5's body for a thing the simulation holds as an ordered list
 * of record ids. **The pages are the silhouette**, and they are the same trick
 * the market board's notices are: two pale leaves are the brightest surface on
 * any prop in this file except a notice, so a book reads as a book from twenty
 * tiles and not as another cairn. The dark cover under them is what makes the
 * pale read as paper rather than as stone.
 *
 * Waist height rather than knee height, unlike a grave — a book is *read*
 * standing up, and a plinth that put the pages at the ankles would be a prop
 * the player has to guess at. The plinth is the same cut stone a headstone is
 * ({@link PALETTE}, grouped by material), because a town that raises one raises
 * the other out of the same quarry.
 *
 * Two variants, cosmetic only: one lately cut, one weathered with the paper
 * gone yellow. The simulation leaves books of its own when a war ends, so a
 * world nobody has played in has some that have stood a while.
 */
function chronicleBoxes(variant: number): readonly Box[] {
  const weathered = variant % 2 === 1;
  const plinth = weathered ? PALETTE.stoneShade : PALETTE.stoneFace;
  const cap = weathered ? PALETTE.stoneBase : PALETTE.stonePale;
  // The two leaves are never the same value: one faces the sun and one faces
  // away, and flat shading gives a horizontal surface nothing else to be told
  // apart by.
  const left = weathered ? PALETTE.paperWorn : PALETTE.paperPale;
  const right = weathered ? shade(PALETTE.paperWorn, 0.9) : PALETTE.paperMid;

  return [
    // Two set stones at the foot, which is what the signpost and the town board
    // use to say "a town raised this" rather than "somebody dropped this here".
    { size: [0.44, 0.4, 0.16], at: [-0.42, 0.06, 0.08], color: PALETTE.cairnMid, tilt: [0, 0, 0.35] },
    { size: [0.34, 0.32, 0.13], at: [0.44, -0.1, 0.065], color: PALETTE.cairnDark, tilt: [0, 0, -0.4] },

    { size: [0.62, 0.5, 0.82], at: [0, 0, 0.41], color: plinth },
    // The cap, proud of the plinth on every side: a made edge, and the shelf the
    // book sits on.
    { size: [0.78, 0.62, 0.09], at: [0, 0, 0.86], color: cap },

    // The cover, opened flat. Its front is local -y like every monument's, so a
    // player walking up to read it is standing at the foot of the page.
    { size: [0.62, 0.42, 0.05], at: [0, -0.02, 0.93], color: PALETTE.bookCover },
    // The two leaves, each canted a little off the flat so the spine reads as a
    // spine. Rotating about y lifts the -x end, so the left leaf leans the
    // opposite way from the right one — the market board's roof, in miniature,
    // and the same failure if they lean together (a valley, not a book).
    {
      size: [0.28, 0.36, 0.035],
      at: [-0.15, -0.02, 0.965],
      color: left,
      tilt: [0, 0.13, 0],
    },
    {
      size: [0.28, 0.36, 0.035],
      at: [0.15, -0.02, 0.965],
      color: right,
      tilt: [0, -0.13, 0],
    },
    // A ribbon down the spine: the prop's one saturated accent, and the only
    // cold colour on it, because a book is a made thing somebody paid for.
    { size: [0.05, 0.5, 0.012], at: [0, -0.06, 0.99], color: PALETTE.dyedCloth },
  ];
}

/**
 * What an army left of a town: a burned sill, fallen beams, rubble and ash.
 *
 * Phase 5 decision 11, drawn. It is deliberately the **burned farmstead's
 * vocabulary at a smaller scale** ({@link burnedFarmsteadBoxes}) — the same
 * char values, the same fallen rafters, the same one warm ember — because the
 * simulation calls both of them the same thing: a sack, with the farmstead on
 * the road out and this inside the gate. Two ruins that did not look related
 * would be the client inventing a second kind of disaster.
 *
 * No chimney, which is the one thing it does *not* borrow, and the reason is
 * the reason the farmstead has one: a stack is what makes a burned building
 * legible from a road away, and this is drawn in a ring around a town centre
 * the player is standing in. Five stacks around a square would be a skyline.
 * What this needs instead is to be walked past and looked down at, so the
 * silhouette is low and broken.
 *
 * Three variants, cosmetic only: which way the beams fell and how much wall is
 * left. Nothing here is semantic — the client does not get to decide that a
 * heap is worse because the record says the toll was higher (devlog #2).
 */
function ruinBoxes(variant: number): readonly Box[] {
  const which = ((variant % 3) + 3) % 3;
  // Which way this heap fell, in radians about z, and how much of a wall stayed
  // up. Three shapes from one box list, rather than three box lists.
  const lean = [0.4, -0.9, 1.6][which] ?? 0;
  const wall = [0.72, 0.34, 0.98][which] ?? 0.6;

  return [
    // The footprint: what a fire leaves is at ground level, so this is most of
    // the prop by area and none of it by silhouette.
    { size: [1.9, 1.5, 0.09], at: [0, 0, 0.045], color: PALETTE.charMid, tilt: [0, 0, lean] },
    { size: [1.5, 1.2, 0.06], at: [0.15, -0.1, 0.1], color: PALETTE.charSoot, tilt: [0, 0, lean] },

    // What is left standing of one wall, sooted rather than clean: a stump of
    // masonry is the only thing here that says *building*.
    {
      size: [1.3, 0.28, wall],
      at: [-0.1, 0.62, wall / 2],
      color: PALETTE.stackSoot,
      tilt: [0, 0, lean],
    },
    // And the course of stone the rest of it came down to.
    {
      size: [0.3, 1.3, 0.24],
      at: [-0.82, -0.1, 0.12],
      color: PALETTE.charDeep,
      tilt: [0, 0, lean],
    },

    // Three beams down, at three angles and — the part only a screenshot tells
    // you — in three different compass directions, so they do not all read as
    // vertical from the one side the photograph is taken from.
    {
      size: [0.15, 0.15, 1.7],
      at: [0.25, 0.15, 0.5],
      color: PALETTE.charDeep,
      tilt: [1.15, 0, 0.6 + lean],
    },
    {
      size: [0.13, 0.13, 1.3],
      at: [-0.3, -0.35, 0.4],
      color: PALETTE.charMid,
      tilt: [-0.9, 0.2, -0.5 + lean],
    },
    {
      size: [0.11, 0.11, 1.05],
      at: [0.55, -0.45, 0.3],
      color: PALETTE.charSoot,
      tilt: [1.3, 0, 1.2 + lean],
    },

    // Rubble out of the wall, and the ash the roof came down as.
    { size: [0.36, 0.32, 0.22], at: [0.75, 0.4, 0.11], color: PALETTE.cairnDark, tilt: [0, 0, -0.4] },
    { size: [0.26, 0.24, 0.16], at: [-0.6, 0.5, 0.08], color: PALETTE.cairnMid, tilt: [0, 0, 0.8] },
    { size: [0.8, 0.6, 0.1], at: [-0.15, -0.3, 0.09], color: PALETTE.ashPale, tilt: [0, 0, lean] },
    // The one warm thing in it, and the reason it does not read as rubble —
    // exactly the farmstead's argument, and the same colour.
    { size: [0.4, 0.3, 0.07], at: [0.05, -0.22, 0.13], color: PALETTE.ember },
  ];
}

/**
 * The tool at a work site: a shaft with something on the end of it, standing in
 * the ground.
 *
 * **The recordless layer's whole visual claim, and it is a small one.** The
 * simulation says *"there is a felling job at this tile and Halvard has taken
 * it"* (`world/types.ts`'s `JobPost`) and nothing else — no progress, no pose,
 * no animation state — so what the client draws is the tool, standing where the
 * work is, leaning the way a thing being used leans. Who is using it is an
 * `npc` prop at the position the local tier gives them, which is a different
 * prop at a different place, and the two together read as somebody working.
 *
 * Five variants for nine job kinds, mapped in `witness/scenes.ts`: the three
 * that are worked at a *tile* get the tool that does it, the five worked at a
 * *bench* share one bench, because which bench it is, is a fact about the
 * building standing there rather than about the order, and the watch's post is
 * its own — a guard stands somewhere rather than works at something.
 *
 * Chest-to-head height, so the head of the tool clears a crowd — this is the
 * one prop in the file whose job is to be findable across a square.
 */
function toilBoxes(variant: number): readonly Box[] {
  const shaft: Box = {
    size: [0.08, 0.08, 1.25],
    at: [0, 0, 0.66],
    color: PALETTE.timberDark,
    // A working lean rather than a planted one. The caller rocks the whole prop
    // about z (`witness/scenes.ts`), and a shaft that was upright would rock
    // around its own axis and read as nothing at all.
    tilt: [0.26, 0, 0],
  };

  switch (((variant % 6) + 6) % 6) {
    // The pick: a head across the shaft, which is the silhouette that says
    // *stone* rather than *stick*.
    case 0:
      return [
        stoneFoot(),
        shaft,
        { size: [0.62, 0.07, 0.09], at: [0, -0.3, 1.24], color: PALETTE.ironDull, tilt: [0.26, 0, 0] },
        { size: [0.12, 0.1, 0.14], at: [0, -0.28, 1.16], color: PALETTE.timberFace, tilt: [0.26, 0, 0] },
      ];

    // The axe: one blade, wide and flat, on the far side of the shaft.
    case 1:
      return [
        stoneFoot(),
        shaft,
        { size: [0.1, 0.3, 0.36], at: [0, -0.34, 1.2], color: PALETTE.ironDull, tilt: [0.26, 0, 0] },
        { size: [0.13, 0.12, 0.16], at: [0, -0.28, 1.16], color: PALETTE.timberFace, tilt: [0.26, 0, 0] },
      ];

    // The sheaf: what a harvest leaves standing while somebody works it. Low,
    // because ground cover is at the ankles and a two-metre wheatstack in a
    // vegetable patch would be a haystack.
    case 2:
      return [
        { size: [0.44, 0.44, 0.1], at: [0, 0, 0.05], color: PALETTE.earthFresh },
        { size: [0.3, 0.3, 0.62], at: [0, 0, 0.36], color: PALETTE.bundle, tilt: [0.12, 0, 0.3] },
        { size: [0.26, 0.26, 0.5], at: [0.12, 0.08, 0.3], color: PALETTE.clothDust, tilt: [0.18, 0, -0.4] },
        { size: [0.34, 0.06, 0.06], at: [0, 0, 0.46], color: PALETTE.timberDark },
      ];

    // The watch's post: a spear planted point-up beside a brazier on a tripod.
    // Nothing about it is a tool being used — it is a place somebody stands,
    // which is what a patrol order *is* (`sim_core`'s `watch`), and the head
    // clears a crowd for the same reason the pick's does.
    case 4:
      return [
        stoneFoot(),
        {
          size: [0.07, 0.07, 1.55],
          at: [0.16, 0, 0.8],
          color: PALETTE.timberDark,
          tilt: [0.08, 0, 0],
        },
        {
          size: [0.09, 0.09, 0.34],
          at: [0.19, 0, 1.7],
          color: PALETTE.ironDull,
          tilt: [0.08, 0, 0],
        },
        // The brazier: three legs read as one wedge at this size, and the coals
        // are the one warm thing in the palette a night scene has.
        { size: [0.3, 0.3, 0.42], at: [-0.34, 0, 0.22], color: PALETTE.timberDark },
        { size: [0.42, 0.42, 0.14], at: [-0.34, 0, 0.5], color: PALETTE.ironDull },
        { size: [0.3, 0.3, 0.07], at: [-0.34, 0, 0.58], color: PALETTE.ember },
      ];

    // The spade, which is both halves of a burial: it lifts a body off a street
    // and it opens the ground at the burial ground, and which of the two it is,
    // is said by the ground it is standing on rather than by a second prop
    // (`witness/scenes.ts`'s `toilVariant` maps both kinds here).
    case 5:
      return [
        stoneFoot(),
        shaft,
        // The blade: a flat plate at the foot of the shaft, turned into the
        // earth. It is the one tool in the table whose head is at the *bottom*,
        // which is the whole of the silhouette's argument.
        {
          size: [0.26, 0.06, 0.34],
          at: [0, 0.1, 0.2],
          color: PALETTE.ironDull,
          tilt: [0.26, 0, 0],
        },
        // And the turned earth beside it, which is what says *digging* rather
        // than *a spade exists*.
        { size: [0.46, 0.34, 0.09], at: [-0.36, -0.1, 0.045], color: PALETTE.earthFresh },
      ];

    // The bench: a top on two legs with a piece on it and a tool beside it,
    // which is what every workshop in the table shares.
    default:
      return [
        { size: [0.14, 0.14, 0.62], at: [-0.36, 0, 0.31], color: PALETTE.timberDark },
        { size: [0.14, 0.14, 0.62], at: [0.36, 0, 0.31], color: PALETTE.timberDark },
        { size: [1.0, 0.6, 0.11], at: [0, 0, 0.67], color: PALETTE.timberFace },
        // The work on it, proud of the top so it catches the light differently.
        { size: [0.34, 0.26, 0.12], at: [-0.14, -0.04, 0.78], color: PALETTE.ironDull },
        // And the hammer leaning against it, which is what says somebody is
        // *at* this bench rather than that a bench exists.
        {
          size: [0.06, 0.06, 0.72],
          at: [0.42, -0.18, 0.42],
          color: PALETTE.timberDark,
          tilt: [0.3, 0, -0.35],
        },
        { size: [0.16, 0.1, 0.12], at: [0.5, -0.29, 0.76], color: PALETTE.ironDull, tilt: [0.3, 0, -0.35] },
      ];
  }
}


/**
 * A body lying on the ground: somebody under a cloth, ankle high and as long as
 * a person.
 *
 * **The geometry is wave 2B's and the kind is the polish pass's**, and the split
 * between those two sentences is the defect this fixes. These boxes were written
 * as a fifth colourway of {@link goodsBoxes} — *"the one colourway that is not a
 * material"* — and no scene ever drew them: a prop's variant is taken modulo
 * {@link VARIANT_COUNT}, `goods` has four, and the corpse's fifth folded back
 * onto index zero. Every body in the world has been drawn as a **heap of
 * rock**. So the shape is kept exactly as it was authored and given a kind of
 * its own, which is also what it should have had — a body is not a family of
 * material, and it wants its own vantage and its own count.
 *
 * Long, low and one shape rather than a heap of three: a pile reads as loose and
 * a body reads as laid out, which is the whole difference. It stays under the
 * grave beside it on purpose — a grave is a stone at the knee and this is a
 * shape at the ankle, which is the difference between *somebody is buried here*
 * and *nobody has come for them*.
 *
 * **Under a cloth rather than drawn**, because this client keeps no likeness of
 * anybody: a living body comes out of the simulation's own stage and tunic
 * ({@link npcBoxes}) and a corpse crosses the wall as an item with no such row.
 * What a town would see is a shape under cloth, and that is what is drawn.
 */
function bodyBoxes(): readonly Box[] {
  return [
    { size: [0.5, 1.15, 0.22], at: [0, 0, 0.11], color: PALETTE.clothPale, tilt: [0, 0, 0.12] },
    // The rise at the shoulder and the fall at the feet, which is what stops it
    // reading as a rolled rug.
    { size: [0.44, 0.3, 0.14], at: [0, 0.36, 0.26], color: PALETTE.clothPale, tilt: [0, 0, 0.12] },
    { size: [0.3, 0.22, 0.1], at: [-0.03, -0.5, 0.2], color: PALETTE.ashPale, tilt: [0, 0, 0.12] },
  ];
}

/**
 * The stone a tool is stood in, shared by the pick and the axe.
 *
 * A prop with nothing under it reads as floating at any distance where the
 * ground's own shading is flat, which is all of them — the same thing the
 * signpost's set stones and the chronicle's are doing, and it is worth the one
 * box for the same reason.
 */
function stoneFoot(): Box {
  return { size: [0.4, 0.36, 0.13], at: [0, 0, 0.065], color: PALETTE.cairnMid, tilt: [0, 0, 0.4] };
}

/**
 * Something lying on the ground: a heap, and what it is made of.
 *
 * The spoil of a dig, a felled log, a bundle of plants, a thing somebody made
 * and put down. Four colourways for four families of material rather than one
 * per item kind, because the client has no material table and no business
 * having one — and **four** rather than the five it carried until the polish
 * pass, because the fifth was a body under a cloth and a body is not a family of
 * material. It has a kind of its own now ({@link bodyBoxes}), which is what it
 * needed to be drawn at all — what a thing is *called* comes across as a whole phrase
 * (`WitnessSource.itemLabel`) and what is drawn is a heap the right colour to
 * be worth walking over to.
 *
 * Ankle height and no taller. It has to read as *loose on the floor* against a
 * grave, a cairn and a book on a plinth, all of which are things somebody
 * placed; a heap that stood at the knee would join them.
 */
function goodsBoxes(variant: number): readonly Box[] {
  switch (((variant % 4) + 4) % 4) {
    // Rock: two boulders and a chip, in the cairn's own stone so a heap of
    // spoil and a stacked cairn are visibly the same quarry.
    case 0:
      return [
        { size: [0.42, 0.38, 0.26], at: [0, 0, 0.13], color: PALETTE.cairnLit, tilt: [0, 0, 0.3] },
        { size: [0.3, 0.28, 0.2], at: [0.22, -0.14, 0.1], color: PALETTE.cairnMid, tilt: [0, 0, -0.6] },
        { size: [0.16, 0.15, 0.11], at: [-0.24, 0.16, 0.055], color: PALETTE.cairnPale, tilt: [0, 0, 0.9] },
      ];

    // Timber: a log lying across the tile, with a shorter one under it.
    case 1:
      return [
        { size: [0.22, 0.9, 0.22], at: [0, 0, 0.11], color: PALETTE.timberFace, tilt: [0, 0, 0.18] },
        { size: [0.18, 0.6, 0.18], at: [0.16, -0.1, 0.28], color: PALETTE.timberDark, tilt: [0, 0, -0.25] },
        // The cut end, pale, which is what says *felled* rather than *fallen*.
        { size: [0.2, 0.05, 0.2], at: [-0.08, 0.44, 0.11], color: PALETTE.paperWorn, tilt: [0, 0, 0.18] },
      ];

    // Growing things: a bundle, tied. The one green in the file, kept dull for
    // the reason the grave's moss is (see {@link PALETTE}).
    case 2:
      return [
        { size: [0.46, 0.34, 0.16], at: [0, 0, 0.08], color: PALETTE.moss, tilt: [0, 0, -0.2] },
        { size: [0.3, 0.26, 0.18], at: [0.1, 0.1, 0.17], color: PALETTE.bundle, tilt: [0, 0, 0.5] },
        { size: [0.34, 0.05, 0.05], at: [0, 0, 0.14], color: PALETTE.timberDark },
      ];

    // Made things: a crate, which is what a client can honestly draw for a
    // blade, a table and a book without claiming to know which. The pale lid is
    // the market board's paper trick at a tenth the size.
    default:
      return [
        { size: [0.44, 0.4, 0.24], at: [0, 0, 0.12], color: PALETTE.timberDark, tilt: [0, 0, 0.15] },
        { size: [0.46, 0.42, 0.05], at: [0, 0, 0.26], color: PALETTE.timberFace, tilt: [0, 0, 0.15] },
        { size: [0.2, 0.02, 0.14], at: [0, -0.21, 0.16], color: PALETTE.paperMid, tilt: [0, 0, 0.15] },
      ];
  }
}

/**
 * A library's shelf: two uprights, three boards, and what is standing on them.
 *
 * The one item in the world a player walks up to and *reads* rather than picks
 * up (`sim_core::ItemKind::is_portable` says so, which is why a shelf cannot be
 * pocketed), so it gets a prop of its own rather than a `goods` heap.
 *
 * **The books on it are drawn as books and not as data.** How many are actually
 * on it is the simulation's answer and it is said in the panel, not in the
 * geometry — the spines here are three because three is what a shelf looks like,
 * exactly as the market board's notices are three (plan, decision 14's
 * empty-shelf invitation is a *sentence*, not an empty prop).
 *
 * Chest height, so the boards are at reading level and the silhouette is a
 * built thing rather than a chest.
 */
function shelfBoxes(): readonly Box[] {
  return [
    { size: [0.12, 0.44, 1.5], at: [-0.5, 0, 0.75], color: PALETTE.timberDark },
    { size: [0.12, 0.44, 1.5], at: [0.5, 0, 0.75], color: PALETTE.timberDark },
    { size: [1.12, 0.44, 0.07], at: [0, 0, 0.06], color: PALETTE.timberDark },
    { size: [1.0, 0.42, 0.06], at: [0, 0, 0.58], color: PALETTE.timberFace },
    { size: [1.0, 0.42, 0.06], at: [0, 0, 1.06], color: PALETTE.timberFace },
    { size: [1.12, 0.44, 0.08], at: [0, 0, 1.48], color: PALETTE.timberDark },
    // Three spines on the middle board, leaning the way books lean when there
    // are not enough of them to hold each other up.
    { size: [0.09, 0.28, 0.34], at: [-0.3, 0, 0.78], color: PALETTE.bookCover },
    { size: [0.08, 0.26, 0.3], at: [-0.18, 0, 0.76], color: PALETTE.bookCover, tilt: [0, 0.16, 0] },
    { size: [0.1, 0.3, 0.32], at: [0.28, 0, 0.77], color: PALETTE.bookCover, tilt: [0, -0.1, 0] },
    // One pale edge, so the dark covers read as *books* rather than as blocks.
    { size: [0.03, 0.26, 0.3], at: [-0.24, 0, 0.76], color: PALETTE.paperPale },
  ];
}

/**
 * Concatenates a prop's boxes into one geometry with baked vertex colours.
 *
 * The same merge `render/chunkMeshes.ts` does for a column's slabs — offset the
 * indices by the vertices already written, and nothing else, because each box
 * has already been moved into the prop's own frame. One geometry per prop kind
 * is one draw call per prop; six separate box meshes would be six.
 */
function buildGeometry(boxes: readonly Box[]): THREE.BufferGeometry {
  const parts: { geometry: THREE.BufferGeometry; color: THREE.Color }[] = [];
  let vertexCount = 0;
  let indexCount = 0;

  for (const box of boxes) {
    const geometry = new THREE.BoxGeometry(box.size[0], box.size[1], box.size[2]);

    if (box.tilt !== undefined) {
      geometry.rotateX(box.tilt[0]);
      geometry.rotateY(box.tilt[1]);
      geometry.rotateZ(box.tilt[2]);
    }

    geometry.translate(box.at[0], box.at[1], box.at[2]);
    geometry.deleteAttribute("uv");

    const position = geometry.getAttribute("position");
    vertexCount += position.count;
    indexCount += geometry.getIndex()?.count ?? position.count;
    // `THREE.Color` converts an sRGB hex into the renderer's linear working
    // space on construction, which is exactly what a vertex colour is — the
    // same contract the mesher's buffers satisfy (`world/types.ts`).
    parts.push({ geometry, color: new THREE.Color(box.color) });
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const indices = new Uint32Array(indexCount);

  let vertexOffset = 0;
  let indexOffset = 0;

  for (const part of parts) {
    const position = part.geometry.getAttribute("position");
    const normal = part.geometry.getAttribute("normal");
    const index = part.geometry.getIndex();

    positions.set(position.array as Float32Array, vertexOffset * 3);
    normals.set(normal.array as Float32Array, vertexOffset * 3);

    for (let vertex = 0; vertex < position.count; vertex += 1) {
      colors[(vertexOffset + vertex) * 3] = part.color.r;
      colors[(vertexOffset + vertex) * 3 + 1] = part.color.g;
      colors[(vertexOffset + vertex) * 3 + 2] = part.color.b;
    }

    if (index === null) {
      // `BoxGeometry` is indexed today; a build that stopped being would still
      // merge rather than draw nothing.
      for (let vertex = 0; vertex < position.count; vertex += 1) {
        indices[indexOffset + vertex] = vertexOffset + vertex;
      }

      indexOffset += position.count;
    } else {
      for (let i = 0; i < index.count; i += 1) {
        indices[indexOffset + i] = index.getX(i) + vertexOffset;
      }

      indexOffset += index.count;
    }

    vertexOffset += position.count;
    part.geometry.dispose();
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  // Computed once for a geometry that is shared by every prop of its kind and
  // never rebuilt, so the O(n) pass `chunkMeshes.ts` avoids per rebuild is paid
  // here exactly once.
  geometry.computeBoundingSphere();

  return geometry;
}

/**
 * Draws one line of text into a canvas texture.
 *
 * No font file and no atlas: the HUD's own monospace stack, rendered at load and
 * cached by its text. A name is a handful of characters and a settlement has a
 * dozen names, so the whole label set is a few hundred kilobytes of texture that
 * never changes.
 */
function drawLabel(text: string): THREE.Texture {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (context === null) {
    // No 2D context is not a reason to lose the prop it belongs to. An empty
    // texture draws nothing and the body underneath is still there.
    return new THREE.CanvasTexture(canvas);
  }

  const fontPixels = Math.round(LABEL_SIZE_TILES * LABEL_PIXELS_PER_TILE * 0.62);
  const font = `${fontPixels}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;

  // Measured before the canvas is sized, because sizing a canvas resets its
  // context — including the font that was just set on it.
  context.font = font;
  const padding = Math.round(fontPixels * 0.45);
  canvas.width = Math.ceil(context.measureText(text).width) + padding * 2;
  canvas.height = Math.round(fontPixels * 1.5);

  context.font = font;
  context.textBaseline = "middle";
  context.fillStyle = LABEL_PLATE;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = LABEL_INK;
  context.fillText(text, padding, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  // The canvas is sRGB and the renderer works in linear; saying so is what stops
  // the plate from coming out washed against the HUD it matches.
  texture.colorSpace = THREE.SRGBColorSpace;

  return texture;
}

/** Multiplies an sRGB hex towards black, for a second tone of the same cloth. */
function shade(color: number, factor: number): number {
  const red = Math.round(((color >> 16) & 0xff) * factor);
  const green = Math.round(((color >> 8) & 0xff) * factor);
  const blue = Math.round((color & 0xff) * factor);

  return (red << 16) | (green << 8) | blue;
}
